import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { 
  Box, 
  Paper, 
  TableContainer,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from 'notistack';
import { isBefore, startOfDay } from 'date-fns';
import { fetchTradeHistory } from '../../../store/actions/orders';
import { formatApiDate } from '../../../utils/dateUtils';
import ExportTradeHistoryModal from './ExportTradeHistoryModal';
import ReportsFilterBar, { FiltersButton, ICON_ONLY_ACTIONS_BELOW_PX } from '../common/ReportsFilterBar';
import ReportTabLayout from '../common/ReportTabLayout';
import ReportsPagination from '../common/ReportsPagination';
import ReportsEmptyState from '../common/ReportsEmptyState';
import ReportsErrorState from '../common/ReportsErrorState';
import ReportsSubTabs from '../common/ReportsSubTabs';
import ScrollingNotice from '../common/ScrollingNotice';
import useReportFilters from '../common/useReportFilters';
import { TRADE_FILTER_DEFS, EXCHANGE_OPTIONS } from './tradeHistoryFilters';
import TradeHistoryTable from './TradeHistoryTable';
import TradeHistoryMobileList from './TradeHistoryMobileList';
import TradeHistoryFilterDrawer from './TradeHistoryFilterDrawer';

const TradeHistoryDetails = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const iconOnlyActions = useMediaQuery(theme.breakpoints.between('sm', ICON_ONLY_ACTIONS_BELOW_PX));
  // Labelled Filters goes in the exchange-tab row; as an icon it stays beside Export.
  const filtersInTabRow = !isMobile && !iconOnlyActions;

  const { enqueueSnackbar } = useSnackbar();

  // Table data state
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  const dispatch = useDispatch();

  const filterProps = useReportFilters({
    defaultPreset: 'week',
    extraFilterDefs: TRADE_FILTER_DEFS,
    onFiltersApplied: (newState) => {
      setPage(0);
      fetchTrades(0, rowsPerPage, newState);
    }
  });

  const {
    startDate,
    endDate,
    extraFilters,
    applyExtraFilter,
    draftExtraFilters,
    setDraftExtraFilter,
    filterDrawerOpen,
    setFilterDrawerOpen,
    handleResetFilters,
    handleApplyFilters
  } = filterProps;

  // The exchange is picked from the sub-tabs, not the drawer, so the Filters
  // badge counts only what the drawer still holds.
  const filterBarProps = {
    ...filterProps,
    activeFilterCount: TRADE_FILTER_DEFS.filter(
      (def) => def.key !== 'exchange' && extraFilters[def.key] !== def.default
    ).length,
  };
  const drawerFilterCount = filterBarProps.activeFilterCount;

  // Trade history holds trades up to yesterday, so the notice shows only when the
  // range reaches today (every preset does).
  const rangeIncludesToday = Boolean(endDate) && !isBefore(startOfDay(endDate), startOfDay(new Date()));

  // Only the newest fetch may update the table, so a slower older response can't
  // overwrite the rows of the exchange clicked after it.
  const latestRequestIdRef = useRef(0);

  // Export modal state
  const [openExportModal, setOpenExportModal] = useState(false);
  
  const fetchTrades = async (newPage, newRowsPerPage, overrideParams = null) => {
    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;
    const isSuperseded = () => requestId !== latestRequestIdRef.current;

    setLoading(true);

    // Clear previous data immediately when starting a new fetch
    setTrades([]);
    setTotalCount(0);
    setLoadError(null);
    
    try {
      const sDate = overrideParams?.startDate || startDate;
      const eDate = overrideParams?.endDate || endDate;
      const exch = overrideParams?.exchange !== undefined ? overrideParams.exchange : extraFilters.exchange;
      const bs = overrideParams?.buySell !== undefined ? overrideParams.buySell : extraFilters.buySell;
      const errTrd = overrideParams?.isErrorTrade !== undefined ? overrideParams.isErrorTrade : extraFilters.isErrorTrade;

      const params = {
        page: newPage + 1,
        pageSize: newRowsPerPage,
        clientIds: localStorage.getItem("login_id"), // Add missing clientIds parameter
        exchange: exch,
        startBusinessDate: formatApiDate(sDate),
        endBusinessDate: formatApiDate(eDate),
        buySell: bs === '2' ? undefined : bs,
        isErrorTrade: errTrd
      };
      
      const result = await dispatch(fetchTradeHistory(params));
      if (isSuperseded()) return;

      // An array (even an empty one) is a successful response. The action
      // resolves with { success: false } instead of rejecting, so a failed
      // request would otherwise fall through and look like "no trades".
      if (Array.isArray(result?.trades)) {
        setTrades(result.trades);
        setTotalCount(result.total_count || 0);
      } else if (Array.isArray(result?.data?.trades)) {
        setTrades(result.data.trades);
        setTotalCount(result.data.total_count || 0);
      } else {
        throw new Error(result?.error || 'Failed to fetch trade history');
      }
    } catch (error) {
      if (isSuperseded()) return;
      console.error("Component error:", error);
      setLoadError(error.message || 'Error fetching trade history');
      enqueueSnackbar(error.message || 'Error fetching trade history', { 
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 3000
      });
    } finally {
      if (!isSuperseded()) setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrades(page, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Leaving the tab supersedes whatever is still in flight, so a late failure
  // can't raise an error toast over the tab the user moved to.
  useEffect(() => () => { latestRequestIdRef.current += 1; }, []);
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchTrades(newPage, rowsPerPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchTrades(0, newRowsPerPage);
  };

  const handleOpenExportModal = () => {
    setOpenExportModal(true);
  };
  
  const handleCloseExportModal = () => {
    setOpenExportModal(false);
  };
  
  return (
    <ReportTabLayout>
      <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2,
            flexShrink: 0,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: '1px solid ' + theme.palette.divider,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5
          }}
        >
        <ReportsFilterBar
          filterProps={filterBarProps}
          onExportClick={handleOpenExportModal}
          showFilterDrawerButton={!filtersInTabRow}
          showActivePills={false}
          showPeriodChip={!isMobile}
          // Phones: Filters/Export as icons on the presets' row, which
          // shortens the presets just enough to make them fit.
          iconOnlyActions={isMobile || iconOnlyActions}
          fitPresetsToRow={isMobile}
        />
        {/* mb cancels the card's bottom padding so the tab underline sits on its edge. */}
        <Box sx={{ borderTop: 1, borderColor: 'divider', mb: -1.5 }}>
          <ReportsSubTabs
            ariaLabel="Exchange"
            options={EXCHANGE_OPTIONS}
            value={extraFilters.exchange}
            onChange={(exchange) => applyExtraFilter('exchange', exchange)}
            action={filtersInTabRow ? (
              <FiltersButton count={drawerFilterCount} onClick={() => setFilterDrawerOpen(true)} />
            ) : undefined}
          />
        </Box>
      </Paper>

      {rangeIncludesToday && (
        // mt pulls it into the filter card's 16px margin, leaving 8px above.
        <ScrollingNotice sx={{ mt: -1, mb: 1 }}>
          Today&apos;s trades will appear here tomorrow. To see them now, go to{' '}
          <Box component="span" sx={{ fontWeight: 600 }}>Orders → Trades</Box>.
        </ScrollingNotice>
      )}

      <TableContainer
        id="trade-history-table-container"
        component={Paper}
        elevation={0}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'auto',
          width: '100%',
          borderRadius: 2,
          border: '1px solid ' + theme.palette.divider,
          bgcolor: 'background.paper',
          willChange: 'scroll-position',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': {
            height: '8px',
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.grey[100],
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.grey[400],
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: theme.palette.grey[500],
            },
          },
        }}
      >
          {/* Results Table */}
          {loading ? (
            isMobile ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              // Desktop keeps the header and fills the body with placeholder
              // rows, so the data drops in without the layout jumping.
              <TradeHistoryTable trades={[]} loading />
            )
          ) : loadError ? (
            <ReportsErrorState
              title="Couldn't Load Trade History"
              message={loadError}
              onRetry={() => fetchTrades(page, rowsPerPage)}
              iconSize={64}
              padding={6}
            />
          ) : trades.length === 0 ? (
            <ReportsEmptyState
              icon={SearchIcon}
              title="No Trade History Found"
              message="No trades found for the selected date."
              iconSize={64}
              padding={6}
            />
          ) : (
            <>
              {isMobile ? (
                // Mobile view - Card layout
                <TradeHistoryMobileList trades={trades} />
              ) : (
                // Desktop view - Table layout
                <TradeHistoryTable trades={trades} />
              )}
            </>
          )}
      </TableContainer>
      
      <ReportsPagination
          show={trades.length > 0}
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      
      <TradeHistoryFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        isMobile={isMobile}
        draftExtraFilters={draftExtraFilters}
        setDraftExtraFilter={setDraftExtraFilter}
        onReset={handleResetFilters}
        onApply={() => { setPage(0); handleApplyFilters(); }}
      />
      
      {/* Export Modal */}
      <ExportTradeHistoryModal
        open={openExportModal}
        onClose={handleCloseExportModal}
        defaultExchange={extraFilters.exchange}
        defaultStartDate={startDate}
        defaultEndDate={endDate}
        title="Export Trade History"
      />
    </ReportTabLayout>
  );
};

export default TradeHistoryDetails; 